import React, { useState, useContext, useEffect } from "react";
import TextInput from "~/shared/TextInput";
import Button from "~/shared/Btn";
import FlatButton from "~/shared/FlatBtn";
import { Header, Content, Footer } from "../styles";
import RangeInput from "~/shared/RangeInput";
import TextareaInput from "~/shared/TextareaInput";
import { Row, Col } from "react-bootstrap";
import { MultiSelectFormContext } from "~/contexts/MultiSelectForm";
import { toast } from "react-toastify";

type RealStateProps = {
    goToStep?: (step: number) => void;
    prevStep?: () => void;
    nextStep?: () => void;
    currentStep?: number;
};

const extraFields = {
    property_type: {
        name: "Tipo do imóvel",
        value: "",
        type: "texto",
    },
    property_size: {
        name: "Tamanho do imóvel",
        value: "",
        type: "numero",
    },
    external_photos: {
        name: "Fotos externas",
        value: "",
        type: "texto",
    },
    qtd_min_photos: {
        name: "Quantidade minima de fotos",
        value: "",
        type: "numero",
    },
    adicional_info: {
        name: "Informações adicionais",
        value: "",
        type: "texto",
    },
};

const RealState = ({ nextStep, goToStep }: RealStateProps) => {
    const { values, setValues, handleChange, confirmationStep, updateValuesInLocalStorage } =
        useContext(MultiSelectFormContext);

    const properties = ["Casa", "Apartamento", "Escritório", "Bar/Restaurante"];
    const qtdFotos = ["10", "15", "20", "25"];

    const [propertyType, setPropertyType] = useState<string>(values?.extra?.property_type?.value || "");
    const [qtdSelected, setQtdSelected] = useState<string>(values?.extra?.qtd_min_photos?.value || "");
    const [externalArea, setExternalArea] = useState<boolean>(values?.extra?.external_photos?.value || false);
    const [squareMeter, setSquareMeter] = useState<string>(values?.extra?.property_size?.value || 50);
    const [addicionalInfo, setAddicionalInfo] = useState<string>(values?.extra?.adicional_info?.value || "");

    const [isEdit, setIsEdit] = useState(false);

    const briefing = `Tipo do imóvel: O job será feito em uma casa, apartamento, loja, conjunto comercial ou prédio?\n ${propertyType} \n\nTamanho do imóvel: Qual o tamanho do imóvel que será fotografados?\n ${squareMeter}m² \n\nÁreas externas: Você quer que seja fotografado a fachada e áreas comuns do imóvel? \n ${
        externalArea ? "Sim" : "Não"
    } \n\n Informações adicionais: \n ${addicionalInfo}`;

    const handleExtraFieldChange = async (name: string, value: any) => {
        if (Object.keys(extraFields).includes(name)) {
            handleChange(name, { ...extraFields[name], value, type: extraFields[name].type }, { extra: true });
            handleChange("briefing", briefing);
        }
    };

    const validateAndNextStep = () => {
        if (confirmationStep) {
            updateValuesInLocalStorage();
            return goToStep(confirmationStep);
        }

        if (!propertyType.length || !qtdSelected || !squareMeter) {
            return toast.error("Todas as informações precisam ser preenchidas.");
        }
        nextStep();
    };

    const setDefaultValues = () => {
        handleChange("property_type", propertyType, { extra: true });
        handleChange("qtd_min_photos", qtdSelected, { extra: true });
        handleChange("external_photos", externalArea, { extra: true });
        handleChange("property_size", squareMeter, { extra: true });
    };

    useEffect(() => {
        const localValues = JSON.parse(localStorage.getItem("values"));
        if (confirmationStep) {
            setValues(localValues);
        } else {
            setDefaultValues();
        }
    }, []);

    return (
        <>
            <Header>
                <h1>Defina o briefing do seu job</h1>
                <span>Precisamos de algumas informações para realizar o seu job!</span>
            </Header>
            <Content>
                <Row>
                    <strong>Tipo de imóvel</strong>
                </Row>
                <Row>
                    {properties.map((proprety) => (
                        <Col lg={3} sm={6} key={proprety}>
                            <FlatButton
                                className={propertyType === proprety && "selected"}
                                onClick={() => {
                                    handleExtraFieldChange("property_type", proprety);
                                    setPropertyType(proprety);
                                }}
                                block
                            >
                                {proprety}
                            </FlatButton>
                        </Col>
                    ))}
                </Row>
            </Content>
            <Content>
                <Row>
                    <strong>Tamanho do seu imóvel em m²</strong>
                    <span>Fachada, área comum, vaga de garagem, etc.</span>
                </Row>
                <Row>
                    <Col lg={10} sm={9}>
                        <RangeInput
                            name="property_size"
                            min="10"
                            max="1000"
                            className="range-input"
                            value={squareMeter}
                            onChange={(name: string, value: string) => {
                                handleExtraFieldChange(name, value);
                                setSquareMeter(value);
                            }}
                        />
                    </Col>
                    <Col lg={2} sm={3}>
                        {!isEdit ? (
                            <FlatButton block onClick={() => setIsEdit(true)}>
                                {squareMeter || 50}m²
                            </FlatButton>
                        ) : (
                            ""
                        )}
                        {isEdit ? (
                            <TextInput
                                name="property_size"
                                value={squareMeter}
                                style={{ fontSize: "1rem", color: "#B7B7B7", textAlign: "center" }}
                                autoFocus
                                onBlur={() => setIsEdit(false)}
                                onChange={(name: string, value: string) => {
                                    handleExtraFieldChange(name, value);
                                    setSquareMeter(value);
                                }}
                            />
                        ) : (
                            ""
                        )}
                    </Col>
                </Row>
            </Content>
            <Content>
                <Row>
                    <strong>Fotos em área externa</strong>
                    <span>Fachada, área comum, vaga de garagem, etc.</span>
                </Row>
                <Row style={{ marginTop: "2rem" }}>
                    <Col sm={6}>
                        <FlatButton
                            className={externalArea && "selected"}
                            onClick={() => {
                                setExternalArea(true);
                                handleExtraFieldChange("external_photos", true);
                            }}
                            block
                        >
                            Sim
                        </FlatButton>
                    </Col>
                    <Col sm={6}>
                        <FlatButton
                            className={!externalArea && "selected"}
                            onClick={() => {
                                setExternalArea(false);
                                handleExtraFieldChange("external_photos", false);
                            }}
                            block
                        >
                            Não
                        </FlatButton>
                    </Col>
                </Row>
            </Content>
            <Content>
                <Row>
                    <strong>Quantidade mínima de fotos</strong>
                </Row>
                <Row>
                    {qtdFotos.map((qtd) => (
                        <Col sm={3} key={qtd}>
                            <FlatButton
                                className={qtdSelected === qtd && "selected"}
                                onClick={() => {
                                    setQtdSelected(qtd);
                                    handleExtraFieldChange("qtd_min_photos", qtd);
                                }}
                                block
                            >
                                {qtd}
                            </FlatButton>
                        </Col>
                    ))}
                </Row>
            </Content>
            <Content>
                <div className="header-content">
                    <p>Informações adicionais</p>
                    <div className="bar" />
                    <TextareaInput
                        name="adicional_info"
                        value={addicionalInfo}
                        rows={5}
                        onChange={(name: string, value: string) => {
                            handleExtraFieldChange(name, value);
                            setAddicionalInfo(value);
                        }}
                    />
                </div>
            </Content>
            <Footer>
                {confirmationStep ? (
                    <Button block onClick={validateAndNextStep}>
                        Salvar
                    </Button>
                ) : (
                    <>
                        <Button id="clickSegmentNext" block onClick={() => handleChange("category", 0)}>
                            Voltar
                        </Button>
                        <Button id="clickSegmentNext" block onClick={validateAndNextStep}>
                            Continuar
                        </Button>
                    </>
                )}
            </Footer>
        </>
    );
};

export default RealState;
